
import { useState, useEffect, useRef } from 'react';

export default function Contact() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="contact" className="py-16 md:py-24 lg:py-32 bg-gradient-to-br from-[#0A1628] via-[#0f1f3a] to-[#061020] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-64 md:w-96 h-64 md:h-96 bg-[#FF6B6B] rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 md:w-96 h-64 md:h-96 bg-[#1a3a5c] rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="absolute top-20 right-10 md:right-20 w-4 h-4 bg-[#FF6B6B] rounded-full animate-ping" style={{ animationDuration: '3s' }}></div>
      <div className="absolute bottom-40 left-10 md:left-20 w-6 h-6 border-2 border-white/20 rounded-full animate-bounce" style={{ animationDuration: '4s' }}></div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-20 text-center">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-flex items-center gap-2 bg-white/10 text-white px-4 md:px-5 py-2 rounded-full text-xs md:text-sm font-semibold mb-4 md:mb-6">
            <span className="w-1.5 h-1.5 bg-[#FF6B6B] rounded-full animate-pulse"></span>
            Contact
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            Ready to <span className="text-[#FF6B6B] italic">start</span>?
          </h2>
          <p className="text-gray-300 text-base md:text-lg lg:text-xl mb-8 md:mb-12 max-w-2xl mx-auto">
            Send me an email and let's discuss your project together. I'll reply within 24 hours.
          </p>

          <a
            href="mailto:hello@webloom-webstudio.com?subject=Project%20Request"
            className="inline-flex items-center gap-3 bg-[#FF6B6B] text-white px-8 md:px-12 py-4 md:py-5 rounded-full text-base md:text-lg font-semibold hover:bg-[#ff5252] transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 cursor-pointer group"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-mail-send-line text-xl md:text-2xl"></i>
            </div>
            <span className="whitespace-nowrap">Send me an email</span>
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </a>

          <div className="mt-8 md:mt-12 flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-8">
            <div className="flex items-center gap-3 text-white/80">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                <i className="ri-mail-line text-lg"></i>
              </div>
              <span className="text-sm md:text-base font-mono">hello@webloom-webstudio.com</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
              <span className="bg-[#FF6B6B] text-white px-2 py-0.5 rounded-full text-xs font-bold animate-pulse">
                24h
              </span>
              <span className="text-gray-200 text-sm">Response time</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
